// External imports
import React, { useRef } from "react";

// Internal imports
import { Modal } from "../Modal";
import {
    AgreementModalContainer,
    AgreementModalContentContainer,
    AgreementModalTitle,
    AgreementModalText,
    AgreeButton,
    ButtonContainer,
    PrintableArea,
    ContentArea,
    AgreementModalLink,
    AgreementModalSubtitle,
    AgreementModalList,
    AgreementModalUnorderList,
} from "./AgreementModal.styles";

// Translation imports
import { useLanguage } from "../../../../core/translations/LanguageContext";
import { SupportedLanguage } from "../../../../core/translations/supportedLanguages";
import { AgreementModalTranslations } from "./translations/types";
import { enTranslations } from "./translations/en";
import { deTranslations } from "./translations/de";
import PrintButton from "../../button/print/PrintButton";
import { useLocation } from "react-router-dom";
// Constants
const TRANSLATIONS: Record<SupportedLanguage, AgreementModalTranslations> = {
    en: enTranslations,
    de: deTranslations,
};

/**
 * Props interface for the AgreementModal component
 * @interface AgreementModalProps
 */
interface AgreementModalProps {
    onRequestClose?: () => void;
    onSubmitAgree?: () => void;
}

/**
 * AgreementModal component
 * Displays a modal on login success
 * @param {AgreementModalProps} props - Component props
 * @returns {React.ReactElement} Rendered component
 */
const AgreementModal: React.FC<AgreementModalProps> = ({ onRequestClose, onSubmitAgree }) => {
    const { language } = useLanguage();
    const translations = TRANSLATIONS[language];
    const componentRef = useRef();
    const location = useLocation();
    const query = location.search.replace(/^\?/, "");

    return (
        <Modal onRequestClose={() => {}}>
            <AgreementModalContainer>
                <AgreementModalContentContainer>
                    {/* @ts-ignore */}
                    <PrintableArea ref={componentRef}>
                        <AgreementModalTitle>
                            {translations.title}
                        </AgreementModalTitle>
                        <ContentArea>
                            {translations.paragraph.map((section) => (
                                <>
                                    <AgreementModalSubtitle>
                                        {section.title}
                                    </AgreementModalSubtitle>
                                    {section.content?.map((paragraph) => (
                                        <AgreementModalText>
                                            {paragraph}
                                        </AgreementModalText>
                                    ))}
                                    {section?.linkContent && (
                                        <AgreementModalText>
                                            {section.linkContent?.start}{" "}
                                            <AgreementModalLink
                                                href={
                                                    section.linkContent
                                                        ?.websiteLink?.href
                                                }
                                            >
                                                {
                                                    section.linkContent
                                                        ?.websiteLink?.text
                                                }
                                            </AgreementModalLink>
                                            <AgreementModalUnorderList>
                                                {section?.linkContent?.points?.map(
                                                    (
                                                        liData: string,
                                                        index: number
                                                    ) => (
                                                        <AgreementModalList
                                                            key={index}
                                                        >
                                                            {liData}
                                                        </AgreementModalList>
                                                    )
                                                )}
                                            </AgreementModalUnorderList>
                                            {section.linkContent?.end}
                                        </AgreementModalText>
                                    )}
                                </>
                            ))}
                        </ContentArea>
                    </PrintableArea>

                    <ButtonContainer>
                        {query != "data-agreement" && (
                            <>
                                <AgreeButton onClick={onSubmitAgree}>
                                    {translations.agreeButton}
                                </AgreeButton>
                                {/* <AgreeButton onClick={onSubmitAgree}>
                                    {translations.denyButton}
                                </AgreeButton> */}
                            </>
                        )}
                        {query == "data-agreement" && <AgreeButton onClick={onRequestClose}>
                            Close
                        </AgreeButton>}
                        {/* @ts-ignore */}
                        <PrintButton printableSection={componentRef} />
                    </ButtonContainer>
                </AgreementModalContentContainer>
            </AgreementModalContainer>
        </Modal>
    );
};

export default AgreementModal;
